import axios from "axios";

const DEFAULT_TTL_MS = 2 * 60 * 1000;
const MAX_ENTRIES = 300;

const cacheEntries = new Map();
const inFlightRequests = new Map();

let cacheInstalled = false;

function stableStringify(value) {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value !== "object") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  }
  const keys = Object.keys(value).sort();
  return `{${keys.map((key) => `${key}:${stableStringify(value[key])}`).join(",")}}`;
}

function authScope(config) {
  try {
    const headers = axios.AxiosHeaders.from(config.headers || {});
    return headers.get("Authorization") || "anonymous";
  } catch (_) {
    return "anonymous";
  }
}

function cacheKey(config, customKey) {
  if (customKey) {
    return `${authScope(config)}::custom::${customKey}`;
  }
  return `${authScope(config)}::${config.url || ""}::${stableStringify(config.params)}`;
}

function cloneResponse(response, configOverride) {
  return {
    data: response.data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
    config: configOverride || response.config,
    request: response.request,
  };
}

function pruneExpiredEntries() {
  const now = Date.now();
  for (const [key, entry] of cacheEntries.entries()) {
    if (entry.expiresAt <= now) {
      cacheEntries.delete(key);
    }
  }
}

function getCachedResponse(key) {
  const entry = cacheEntries.get(key);
  if (!entry) {
    return null;
  }
  if (entry.expiresAt <= Date.now()) {
    cacheEntries.delete(key);
    return null;
  }
  return entry.response;
}

function setCachedResponse(key, response, ttlMs) {
  if (ttlMs <= 0) {
    return;
  }
  pruneExpiredEntries();
  if (cacheEntries.has(key)) {
    cacheEntries.delete(key);
  }
  cacheEntries.set(key, {
    expiresAt: Date.now() + ttlMs,
    response: cloneResponse(response),
  });
  while (cacheEntries.size > MAX_ENTRIES) {
    const oldestKey = cacheEntries.keys().next().value;
    cacheEntries.delete(oldestKey);
  }
}

function resolveAdapter(config) {
  if (typeof axios.getAdapter === "function") {
    return axios.getAdapter(config.adapter || axios.defaults.adapter);
  }
  if (typeof config.adapter === "function") {
    return config.adapter;
  }
  if (typeof axios.defaults.adapter === "function") {
    return axios.defaults.adapter;
  }
  throw new Error("Unable to resolve axios adapter");
}

function resolveCacheOptions(config) {
  const method = (config.method || "get").toLowerCase();
  if (method !== "get") {
    return { enabled: false, ttlMs: 0, key: null };
  }
  const hasExplicitCache =
    config.cache === true
    || typeof config.cache === "number"
    || (typeof config.cache === "object" && config.cache !== null)
    || typeof config.cacheTtlMs === "number";

  if (!hasExplicitCache || config.cache === false) {
    return { enabled: false, ttlMs: 0, key: null };
  }

  let ttlMs = DEFAULT_TTL_MS;
  let key = null;

  if (typeof config.cache === "number") {
    ttlMs = config.cache;
  } else if (typeof config.cache === "object" && config.cache !== null) {
    if (typeof config.cache.ttl === "number") {
      ttlMs = config.cache.ttl;
    }
    if (typeof config.cache.key === "string") {
      key = config.cache.key;
    }
  } else if (typeof config.cacheTtlMs === "number") {
    ttlMs = config.cacheTtlMs;
  }

  return {
    enabled: true,
    ttlMs: Math.max(0, ttlMs),
    key: key,
  };
}

function invalidateScope(config) {
  const scope = authScope(config);
  for (const key of cacheEntries.keys()) {
    if (key.startsWith(`${scope}::`)) {
      cacheEntries.delete(key);
    }
  }
  for (const key of inFlightRequests.keys()) {
    if (key.startsWith(`${scope}::`)) {
      inFlightRequests.delete(key);
    }
  }
}

function onRequest(config) {
  const options = resolveCacheOptions(config);
  if (!options.enabled) {
    return config;
  }

  const key = cacheKey(config, options.key);
  const cachedResponse = getCachedResponse(key);
  if (cachedResponse) {
    config.adapter = async () => cloneResponse(cachedResponse, config);
    return config;
  }

  const adapter = resolveAdapter(config);
  config.adapter = async (adapterConfig) => {
    const pendingRequest = inFlightRequests.get(key);
    if (pendingRequest) {
      const sharedResponse = await pendingRequest;
      return cloneResponse(sharedResponse, adapterConfig);
    }

    const requestPromise = adapter(adapterConfig)
      .then((response) => {
        setCachedResponse(key, response, options.ttlMs);
        return response;
      })
      .finally(() => {
        inFlightRequests.delete(key);
      });

    inFlightRequests.set(key, requestPromise);
    const response = await requestPromise;
    return cloneResponse(response, adapterConfig);
  };

  return config;
}

function onResponse(response) {
  const method = (response?.config?.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].indexOf(method) !== -1) {
    invalidateScope(response.config);
  }
  return response;
}

function onError(error) {
  return Promise.reject(error);
}

export function setupAxiosCache() {
  if (cacheInstalled) {
    return;
  }
  axios.interceptors.request.use(onRequest);
  axios.interceptors.response.use(onResponse, onError);
  cacheInstalled = true;
}

export function clearAxiosCache() {
  cacheEntries.clear();
  inFlightRequests.clear();
}
